<script lang="ts">
import { computed, defineComponent, type PropType } from 'vue'
import { RouterLink, type RouteLocationRaw } from 'vue-router'

type Variant = 'primary' | 'secondary' | 'solid' | 'white'

export default defineComponent({
  name: 'BaseButton',
  props: {
    variant: {
      type: String as PropType<Variant>,
      default: 'primary'
    },
    to: {
      type: [String, Object] as PropType<RouteLocationRaw>,
      default: undefined
    },
    href: {
      type: String,
      default: undefined
    },
    type: {
      type: String as PropType<'button' | 'submit' | 'reset'>,
      default: 'button'
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  setup(props, { attrs }) {
    const isRouterLink = computed(() => props.to !== undefined && props.to !== null)
    const isAnchor = computed(() => !isRouterLink.value && !!props.href)

    const tag = computed(() => {
      if (isRouterLink.value) return RouterLink
      if (isAnchor.value) return 'a'
      return 'button'
    })

    const variantClass = computed(() => {
      switch (props.variant) {
        case 'primary':
          return 'btn-primary'
        case 'secondary':
          return 'btn-secondary'
        case 'white':
          return 'btn-white'
        case 'solid':
          return 'btn'
        default:
          return 'btn-primary'
      }
    })

    const bindProps = computed(() => {
      const base = { ...attrs }

      if (isRouterLink.value) return { ...base, to: props.to }
      if (isAnchor.value) return { ...base, href: props.href }
      return { ...base, type: props.type, disabled: props.disabled }
    })

    return { tag, variantClass, bindProps }
  }
})
</script>

<template>
  <component :is="tag" v-bind="bindProps" :class="variantClass">
    <slot />
  </component>
</template>
